// =============================================================================
// domain/tierFeatures.ts — the Free vs Pro comparison, as data.
// -----------------------------------------------------------------------------
// Relocated from the old client's `tierFeatureRows()` (app/index.html:49092). #65.
//
// ── 🚨 THESE ROWS ARE A PROMISE, NOT DECORATION ──
//
// Every row is something a buyer is told they get. The old client's free-tier
// suite cross-checks each one against the real tab registry — anything promised
// FREE must map to an ungated tab, anything promised PRO must map to a gated one
// — because a comparison table that disagrees with the app is how somebody pays
// and finds the thing they paid for locked, or free.
//
// ⚠ THE NUMBERS ARE INTERPOLATED, never typed. "3 each" reads correctly today
// and silently lies the day a cap moves.
//
// ⚠ "N each" is only true WHILE THE TWO LIMITS AGREE. They are separate
// constants precisely so they could diverge, so this renders whatever they
// actually say rather than assuming.
//
// ── ✅ PRACTICE IS SOLD NOW, AND THE PARAGRAPH THAT USED TO SIT HERE WAS FALSE ──
//
// It said `practice` and `backtest-dashboard` were "BOTH in the sidebar, BOTH
// marked Pro, and NEITHER has a renderer", so a buyer would meet a paywall for a
// blank screen. Measured: both render. `PracticeTab` is 331 lines with its own
// suite, `ResultsTab` 247 with six panels beside it, and both are routed in
// `App.tsx`. Practice was ported under D230 — whose own header records that the
// issue commissioning it made the SAME false claim, from the same empty
// `<section>` tag in the old file that every tab has.
//
// 🚨 That is twice this one claim has been repeated as fact. It came off a stale
// note, not off the code, and the only reason it survived here is that a row
// nobody sells is a row nobody checks.
//
// So Practice is a Pro row like any other: it is built, it is gated, and a free
// user meeting its padlock can now find out what it is. The old client hides it
// from every sidebar and calls it "In development" — that is the OLD client's
// state, and this is the one that launches (D56).
// =============================================================================

import {
  FREE_ACCOUNT_LIMIT, FREE_EXIT_MODEL_LIMIT, FREE_PATTERN_LIMIT,
} from './freeTier';

export interface TierRow {
  /** Rendered bold, before the label. The three method steps use it. */
  lead?: string;
  label: string;
  /** `true` a tick, `false` a dash, a string its own words. */
  free: boolean | string;
  pro: boolean | string;
}

export function tierFeatureRows(): TierRow[] {
  return [
    { label: 'Trading accounts', free: String(FREE_ACCOUNT_LIMIT), pro: 'Unlimited' },
    { label: 'Import trades · journal · calendar', free: true, pro: true },
    { label: 'Dashboard KPIs & equity curve', free: true, pro: true },
    { label: 'Weekly & monthly Review', free: true, pro: true },
    { label: 'Best Opps — the trades you didn’t take', free: true, pro: true },
    // 🔴 NO MARKET INTEL ROW. It was promised FREE here and delivered by neither
    // client — the tab is out of this client's registry on a LICENCE hold, so a
    // free user read "Market Intel ✓" on the buy screen and found no such
    // screen (#90). The promise comes down until the licence lands; putting the
    // row back is part of shipping the tab, not a separate copy change.
    { lead: 'Spot it', label: ' — your #1 performance leak, in dollars', free: true, pro: true },
    { lead: 'Fix it', label: ' — a conditional rule per leak', free: false, pro: true },
    { lead: 'Prove it', label: ' — adherence + verdict', free: false, pro: true },
    {
      label: 'Patterns & Exit Models',
      // ⚠ See the header — do not simplify this to a single number.
      free: FREE_PATTERN_LIMIT === FREE_EXIT_MODEL_LIMIT
        ? `${FREE_PATTERN_LIMIT} each`
        : `${FREE_PATTERN_LIMIT} patterns · ${FREE_EXIT_MODEL_LIMIT} exit models`,
      pro: 'Unlimited',
    },
    { label: 'Excursion — execution analytics', free: false, pro: true },
    { label: 'Setup Quality — pattern, exit model & confluence analytics', free: false, pro: true },
    { label: 'Prop Report — rules-aware prop account performance', free: false, pro: true },
    { label: 'Custom Analytics — create your own dashboard', free: false, pro: true },
    { label: 'Day Overview — the per-session report', free: false, pro: true },
    { label: 'Backtesting & Results', free: false, pro: true },
    { label: 'Practice — replay your own trades and grade the execution', free: false, pro: true },
    { label: 'AI Coach', free: false, pro: true },
  ];
}
